import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, Laptop, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  keepMine,
  keepTheirs,
  resolveConflict,
  subscribeConflicts,
  type ResolutionChoice,
  type SyncConflict,
} from "@/lib/sync-conflicts";

function labelFor(field: string) {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayValue(v: unknown) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function entityName(table: string) {
  if (table === "appointments") return "Appointment · অ্যাপয়েন্টমেন্ট";
  if (table === "invoices") return "Invoice · ইনভয়েস";
  return table;
}

/**
 * Shown when the same appointment or invoice was edited on two devices.
 * The doctor picks, field by field, which version wins.
 */
export function ConflictResolver() {
  const qc = useQueryClient();
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [choices, setChoices] = useState<Record<string, ResolutionChoice>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeConflicts(setConflicts), []);

  const current = conflicts[0];

  useEffect(() => {
    if (!current) return setChoices({});
    const next: Record<string, ResolutionChoice> = {};
    current.fields.forEach((f) => {
      next[f.field] = "mine";
    });
    setChoices(next);
  }, [current?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const when = useMemo(
    () => (current ? new Date(current.queued_at).toLocaleString() : ""),
    [current],
  );

  if (!current) return null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["appointments"] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["queue"] });
  };

  const run = async (fn: () => Promise<void>, message: string) => {
    setBusy(true);
    try {
      await fn();
      refresh();
      toast.success(message);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Sync conflict · সিঙ্ক দ্বন্দ্ব
          </DialogTitle>
          <DialogDescription>
            {entityName(current.table)} — this record was changed on another device
            after your edit ({when}). Choose which value to keep for each field.
            {conflicts.length > 1 && (
              <Badge variant="secondary" className="ml-2">
                +{conflicts.length - 1} more
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-3">
          <div className="space-y-3">
            {current.fields.map((f) => {
              const picked = choices[f.field] ?? "mine";
              return (
                <div key={f.field} className="rounded-xl border border-border p-3">
                  <div className="mb-2 text-sm font-medium">{labelFor(f.field)}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setChoices((c) => ({ ...c, [f.field]: "mine" }))}
                      className={`rounded-lg border p-3 text-left transition ${
                        picked === "mine"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Laptop className="h-3.5 w-3.5" />
                        This device · আপনার পরিবর্তন
                        {picked === "mine" && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
                      </div>
                      <div className="break-words text-sm">{displayValue(f.mine)}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setChoices((c) => ({ ...c, [f.field]: "theirs" }))}
                      className={`rounded-lg border p-3 text-left transition ${
                        picked === "theirs"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Smartphone className="h-3.5 w-3.5" />
                        Other device · সার্ভারে সংরক্ষিত
                        {picked === "theirs" && (
                          <Check className="ml-auto h-3.5 w-3.5 text-primary" />
                        )}
                      </div>
                      <div className="break-words text-sm">{displayValue(f.theirs)}</div>
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Before edit: {displayValue(f.base)}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => run(() => keepTheirs(current.key), "Other device's version kept")}
          >
            Keep server version
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => run(() => keepMine(current.key), "Your version applied")}
          >
            Keep all my changes
          </Button>
          <Button
            disabled={busy}
            onClick={() => run(() => resolveConflict(current.key, choices), "Conflict resolved")}
          >
            Apply selection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConflictResolver;
